import React, { useState } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { useSelector } from 'hooks/redux'
import { Header, Button } from 'components/patterns'
import { getAuthenticatedModulesForVehicleUpdate } from 'selectors/authorization/checPlus'
import { uniq } from 'ramda'
import { validateDynamicContent } from 'services/features'
import AddRemoveFeaturesTable from '../AddRemoveFeaturesTable'
import KeepOrRemoveSwitches from '../Modals/KeepOrRemoveSwitches'
import {
  getKeepOrRemoveSwitchTableData,
  getUpdatedKeepOrRemoveSwitches,
  resolveFeatureUri,
  resolvePreviousUri,
  urisFilteredBy,
} from '../utils'
import { ContentContainer, Footer } from './FeatureCollections.style'
import DynamicFeatureValidationModal from '../DynamicFeatureValidationModal'

const ReviewFeatures = ({
  serialNumber,
  changeStep,
  onGoingSet,
  reset,
  mappedIoSwitches,
  setKeepOrRemoveSwitches,
  onRemoveFeatures,
}) => {
  const [isKeepOrRemoveModalOpen, setKeepOrRemoveModalOpen] = useState(false)
  const [isValidatingDynamicFeatures, setIsValidatingDynamicFeatures] =
    useState(false)
  const [dynamicValidationData, setDynamicValidationData] = useState([])

  const addedFeatures = onGoingSet
    .filter((f) => f.isMarkedForAddition)
    .map(resolveFeatureUri)
    .filter(Boolean)
  const removedFeatures = uniq([
    ...urisFilteredBy('isMarkedForDeletion')(onGoingSet),
    ...onGoingSet
      .filter((f) => f.isMarkedForUpdate)
      .map(resolvePreviousUri)
      .filter(Boolean),
  ])

  const authenticatedModuleNames = useSelector(
    getAuthenticatedModulesForVehicleUpdate
  )

  const [keepRemoveSwitchTableData, removedIOsWithSwitchesList] =
    getKeepOrRemoveSwitchTableData(onGoingSet, mappedIoSwitches)

  const handleCancel = () => {
    reset()
    changeStep(0)
  }

  const handleDynamicValidation = async () => {
    if (!addedFeatures.length && !removedFeatures.length) {
      setDynamicValidationData([])
      changeStep(4)
      setKeepOrRemoveModalOpen(false)
      return
    }

    setIsValidatingDynamicFeatures(true)
    try {
      const response = await validateDynamicContent({
        payload: {
          addedFeatures,
          removedFeatures,
        },
        serialNumber,
      })
      if (response.data.length === 0) {
        setDynamicValidationData([])
        changeStep(4)
        setKeepOrRemoveModalOpen(false)
      } else {
        setDynamicValidationData(response.data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsValidatingDynamicFeatures(false)
    }
  }

  const handleCommitToVehicle = () => {
    const shouldOpenModal =
      !isKeepOrRemoveModalOpen && Boolean(keepRemoveSwitchTableData.length)

    if (shouldOpenModal) {
      setKeepOrRemoveModalOpen(true)
    } else {
      handleDynamicValidation()
    }
  }

  const onKeepOrRemoveModalContinue = (selectedKeepOrRemoveSwitches) => {
    setKeepOrRemoveModalOpen(false)

    const updatedKeepOrRemoveSwitches = getUpdatedKeepOrRemoveSwitches(
      removedIOsWithSwitchesList,
      selectedKeepOrRemoveSwitches
    )
    setKeepOrRemoveSwitches(updatedKeepOrRemoveSwitches)
    handleDynamicValidation()
  }

  const onKeepOrRemoveModalCancel = () => {
    setKeepOrRemoveModalOpen(false)
  }

  return (
    <>
      <ContentContainer className="px-18">
        <Header type="h3">{strings.features_serial(serialNumber)}</Header>
        <AddRemoveFeaturesTable
          isEditable
          features={onGoingSet}
          canRemoveAsBuiltFeature={false}
          authenticatedModuleNames={authenticatedModuleNames}
          onCustomColClick={onRemoveFeatures}
          showDefaultRemove
          hideAlertInfo
          serialNumber={serialNumber}
        />
      </ContentContainer>

      <Footer>
        <Button text={strings.cancel} type="secondary" onClick={handleCancel} />
        <Button
          text={
            isValidatingDynamicFeatures
              ? strings.validating_features
              : strings.commit_to_vehicle
          }
          loading={isValidatingDynamicFeatures}
          disabled={
            isValidatingDynamicFeatures ||
            (!addedFeatures?.length && !removedFeatures?.length)
          }
          onClick={handleCommitToVehicle}
        />
      </Footer>

      <KeepOrRemoveSwitches
        isOpen={isKeepOrRemoveModalOpen}
        onClickCancel={onKeepOrRemoveModalCancel}
        onClickContinue={onKeepOrRemoveModalContinue}
        removedSwitches={removedIOsWithSwitchesList}
        onGoingSet={onGoingSet}
        serialNumber={serialNumber}
      />

      <DynamicFeatureValidationModal
        data={dynamicValidationData}
        serialNumber={serialNumber}
        onClose={() => setDynamicValidationData([])}
      />
    </>
  )
}

ReviewFeatures.propTypes = {
  serialNumber: PropTypes.string,
  changeStep: PropTypes.func,
  reset: PropTypes.func,
  onGoingSet: PropTypes.array,
  step: PropTypes.number,
  mappedIoSwitches: PropTypes.object,
  setKeepOrRemoveSwitches: PropTypes.func,
  onRemoveFeatures: PropTypes.func,
}

export default ReviewFeatures
